<script lang="ts">
  import { onDestroy, untrack } from "svelte"
  import { mdiEmailArrowRightOutline } from "@mdi/js"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import SectionTitle from "@kontextwerk/web-sdk/ui/typography/SectionTitle.svelte"
  import {
    updateUserSettings,
    type DailyBriefingPreferences,
  } from "../../../../../../../../api/kehrblatt/user-settings"
  import SendBriefingDialog from "./_modules/send-briefing-dialog"
  import { sendDailyBriefing } from "../../../../../../../../api/kehrblatt/daily-briefing"

  const {
    organizationId,
    timezone,
    recipient,
    canSend,
    isSelf,
    organizationUnsettled,
    pageSaving = false,
    onStateChange,
  } = $props<{
    organizationId: string
    timezone: string
    recipient: {
      id: string
      label: string
      email: string
      role: string
      preferences: DailyBriefingPreferences
    }
    canSend: boolean
    isSelf: boolean
    organizationUnsettled: boolean
    pageSaving?: boolean
    onStateChange: (state: { dirty: boolean; busy: boolean }) => void
  }>()
  const readDraft = (value: DailyBriefingPreferences) => ({
    enabled: String(value.enabled),
    includePdf: String(value.include_pdf),
    includeOperations:
      value.include_operations === null ? "role" : String(value.include_operations),
  })
  let lastSaved = $state(untrack(() => readDraft(recipient.preferences)))
  let draft = $state(untrack(() => ({ ...lastSaved })))
  let saving = $state(false)
  let sending = $state(false)
  let error = $state("")
  let notice = $state("")
  let showSendDialog = $state(false)
  let sendError = $state("")
  let attempt: { date: string; id: string } | null = null
  let active = true
  const controller = new AbortController()
  const dirty = $derived(
    draft.enabled !== lastSaved.enabled ||
      draft.includePdf !== lastSaved.includePdf ||
      draft.includeOperations !== lastSaved.includeOperations,
  )
  const busy = $derived(saving || sending)
  const disabled = $derived(busy || pageSaving)
  const foreignOptout = $derived(!isSelf && lastSaved.enabled === "false")
  const sendBlockedReason = $derived(
    dirty || organizationUnsettled
      ? "Bitte zuerst speichern"
      : foreignOptout
        ? "Dieser Empfänger hat die Morgenübersicht abbestellt."
        : "",
  )
  const activationOptions = [
    { value: "true", label: "Aktiviert" },
    { value: "false", label: "Deaktiviert" },
  ]
  $effect(() => onStateChange({ dirty, busy }))
  onDestroy(() => {
    active = false
    controller.abort()
  })

  /** Save this recipient's draft for the page action, using the normalized server result. */
  export async function saveChanges(): Promise<boolean> {
    if (!dirty) return true

    if (busy) return false

    saving = true
    error = notice = ""
    try {
      const result = await updateUserSettings(recipient.id, {
        daily_briefing: {
          enabled: draft.enabled === "true",
          include_pdf: draft.includePdf === "true",
          include_operations:
            draft.includeOperations === "role" ? null : draft.includeOperations === "true",
        },
      })

      if (!active) return false

      if (!result.daily_briefing) throw new Error("Missing saved preferences")

      lastSaved = readDraft(result.daily_briefing)
      draft = { ...lastSaved }
      notice = "Persönliche Einstellungen gespeichert."

      return true
    } catch {
      if (active) error = "Speichern fehlgeschlagen. Ihre Änderungen bleiben erhalten."

      return false
    } finally {
      if (active) saving = false
    }
  }
  async function send(referenceDate: string) {
    if (!canSend || disabled || sendBlockedReason) return

    sending = true
    sendError = notice = ""

    if (!attempt || attempt.date !== referenceDate) {
      attempt = { date: referenceDate, id: crypto.randomUUID() }
    }

    try {
      await sendDailyBriefing(
        organizationId,
        recipient.id,
        attempt.id,
        controller.signal,
        referenceDate,
      )

      if (!active) return

      attempt = null
      showSendDialog = false
      notice = recipient.email
        ? `Versand an ${recipient.email} wurde angestoßen.`
        : "Versand an Ihre Konto-E-Mail-Adresse wurde angestoßen."
    } catch {
      if (active) sendError = "Der Versand konnte nicht bestätigt werden. Bitte erneut versuchen."
    } finally {
      if (active) sending = false
    }
  }
</script>

<section class="settings-stack" aria-label={recipient.label}>
  <SectionTitle
    title={recipient.label}
    description={recipient.email}
    brightBackground={true}
    small={true}
  >
    {#snippet buttonSnippet()}
      {#if canSend}
        <CTA
          type="secondaryButton"
          content={sending ? "Wird angefordert …" : "Jetzt senden"}
          icon={mdiEmailArrowRightOutline}
          darkMode={false}
          onClick={() => {
            sendError = ""
            showSendDialog = true
          }}
          disabled={disabled || Boolean(sendBlockedReason)}
          tooltipContent="Datum wählen und die Übersicht einmalig an die hinterlegte E-Mail-Adresse senden. Der automatische Versand bleibt unverändert."
        />
      {/if}
    {/snippet}
  </SectionTitle>
  <div class="box-content form">
    <div class="settings-row">
      <div class="field-block">
        <Input
          type="select"
          placeholder="Morgenübersicht erhalten"
          bind:value={draft.enabled}
          options={activationOptions}
          clearable={false}
          brightBackground={true}
          {disabled}
          tooltip="Automatische Morgenübersicht an die hinterlegte Konto-E-Mail-Adresse senden."
        />
      </div>
      <div class="field-block">
        <Input
          type="select"
          placeholder="Tagesplan als PDF"
          bind:value={draft.includePdf}
          options={activationOptions}
          clearable={false}
          brightBackground={true}
          {disabled}
          tooltip="Jeder versendeten Übersicht den Tagesplan anhängen, auch ohne eigene Termine. Bei Teamzugriff enthält das PDF zusätzlich die freigegebenen Mitarbeiterpläne; der persönliche Plan steht zuerst."
        />
      </div>
      <div class="field-block">
        <Input
          type="select"
          placeholder="Betriebsübersicht"
          bind:value={draft.includeOperations}
          options={[{ value: "role", label: "Standard gemäß Rolle" }, ...activationOptions]}
          clearable={false}
          brightBackground={true}
          {disabled}
          tooltip="Für Organisationsadmins standardmäßig aktiviert. Inhalte bleiben auf die vorhandenen Datenberechtigungen begrenzt."
        />
      </div>
    </div>
  </div>
  {#if error}<p role="alert">{error}</p>{/if}
  {#if notice}<p role="status">{notice}</p>{/if}
  {#if canSend && sendBlockedReason}<p class="helper-text">{sendBlockedReason}</p>{/if}
</section>
{#if showSendDialog}
  <SendBriefingDialog
    {timezone}
    recipientLabel={recipient.label}
    recipientEmail={recipient.email}
    {sending}
    disabled={!canSend || disabled || Boolean(sendBlockedReason)}
    error={sendError}
    onSend={send}
    onClose={() => (showSendDialog = false)}
  />
{/if}

<style>
  .settings-stack,
  .box-content {
    display: flex;
    flex-direction: column;
    gap: 0.9rem;
    min-width: 0;
  }
  .settings-stack {
    padding-top: 1rem;
    overflow-wrap: anywhere;
  }
  .settings-row {
    display: grid;
    width: 100%;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 13rem), 1fr));
    gap: 0.9rem;
  }
  .field-block {
    min-width: 0;
  }
  p {
    margin: 0;
    color: var(--text-invers-100);
  }
  .helper-text {
    font-size: 0.85rem;
  }
  [role="alert"] {
    border-left: 3px solid var(--red-300);
    padding: 0.6rem 0.8rem;
  }
</style>
